/**
 * LIGHTNING YARD v2 — Quake 3 "Longest Yard" homage inside an orbital dock bay.
 *
 *  - Floating platforms + GENESIS tower, connected by wide grated ramps.
 *  - Guardrails on every platform edge (openings only at ramps) — no more
 *    sliding into the void by accident. Rails are jumpable on purpose (Q3 feel).
 *  - Catch deck under the whole arena: falling = landing on the lower deck and
 *    riding a jump pad back up, not dying.
 *  - Station hull enclosure with window bands to deep space, pillars, light strips,
 *    animated data panels, live block-height hologram.
 *  All static geometry is AABB (fast, deterministic collision — like Q3 brushes).
 */

import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { pbr, glass, SOLID, MaterialName } from '../render/materials';

export interface Box { min: THREE.Vector3; max: THREE.Vector3 }

/** Monde collisionnable interrogeable par broadphase (implémenté par LightningYard). */
export interface SolidWorld {
  queryBox(min: THREE.Vector3, max: THREE.Vector3, out: Box[]): Box[];
}

export interface JumpPad {
  position: THREE.Vector3;
  radius: number;
  velocity: THREE.Vector3;
  mesh: THREE.Mesh;
  ring: THREE.Mesh;
  lastTrigger: number;
}

export interface Teleporter {
  position: THREE.Vector3;
  radius: number;
  destination: THREE.Vector3;
  destYaw: number;
  mesh: THREE.Mesh;
}

export type ItemType = 'health' | 'armor' | 'ammo' | 'sats' | 'quad';

export interface ItemSpawn {
  type: ItemType;
  position: THREE.Vector3;
  mesh: THREE.Group;
  respawnAt: number;
  amount: number;
}

export interface SpawnPoint { position: THREE.Vector3; yaw: number }

/** Opening in a guardrail: which edge, center along the edge, half-width. */
interface RailOpening { edge: 'n' | 's' | 'e' | 'w'; at: number; half: number }

export class LightningYard implements SolidWorld {
  public readonly group = new THREE.Group();
  public readonly solids: Box[] = [];
  public readonly jumpPads: JumpPad[] = [];
  public readonly teleporters: Teleporter[] = [];
  public readonly items: ItemSpawn[] = [];
  public readonly spawns: SpawnPoint[] = [];
  /** Below the catch deck — only reachable if something goes very wrong. */
  public readonly killZ = -40;
  public readonly deckY = -6;
  public readonly enclosureHalf = 48;

  private animated: Array<(t: number, dt: number) => void> = [];
  private holoCanvas!: HTMLCanvasElement;
  private holoTex!: THREE.CanvasTexture;
  private blockHeight: number | null = null;
  private dataPanels: Array<{ ctx: CanvasRenderingContext2D; tex: THREE.CanvasTexture; seed: number }> = [];
  private dataT = 0;

  // shared materials (cheap to reuse)
  private railMat = SOLID.gunmetal();
  private railGlow = pbr('trim', { emissiveIntensity: 1.8 });
  private postMat = SOLID.brushed();
  private trimMat = pbr('trim');
  private studMat = SOLID.glow(0x9be7ff, 1.6);
  private rampEdgeMat = SOLID.glow(0xf7931a, 1.4);
  private rampMats = new Map<MaterialName, THREE.MeshStandardMaterial>();
  private padGlowMat = new THREE.MeshStandardMaterial({ color: 0x001010, emissive: 0x00e5ff, emissiveIntensity: 3.0, roughness: 0.4 });

  // Broadphase : grille XZ des solides (cellules de 4 m). Sans elle, chaque
  // requête sol/collision balayait les ~440 AABB de la map.
  private readonly CELL = 4;
  private grid: Box[][] = [];
  private gridMinX = 0; private gridMinZ = 0; private gridNX = 0; private gridNZ = 0;
  private static readonly EMPTY: Box[] = [];
  // Pool fixe de PointLights. La map en allouait 12 (5 anti-grav, 2 téléporteurs,
  // 1 cœur, 4 projecteurs) qui s'ajoutaient à la volée pendant le build ; avec les
  // 6 du pool FX la scène montait à 18 lumières, chacune payée par fragment dans
  // tous les shaders. Ici : 6 slots créés AVANT la géométrie, jamais ajoutés ni
  // retirés ensuite → compte de lumières constant, zéro recompilation de shader.
  // Le pool de render/fx.ts (flashes, explosions) reste indépendant de celui-ci.
  private static readonly LIGHT_SLOTS = 6;
  /** slot 0 = cœur GENESIS · 1 = anti-grav plateforme centrale · 2-5 = projecteurs de dock */
  private static readonly SLOT = { CORE: 0, TOWER: 1, BAY: 2 } as const;
  private lightPool: THREE.PointLight[] = [];

  /** Matériaux PBR mutualisés : sans ça chaque block() créait son propre
   *  MeshStandardMaterial → un groupe de fusion par bloc, donc aucune fusion. */
  private matCache = new Map<string, THREE.MeshStandardMaterial>();
  private glowCache = new Map<string, THREE.MeshStandardMaterial>();
  private hullGlass?: THREE.MeshPhysicalMaterial;
  private hullStrips?: THREE.MeshStandardMaterial;
  private pillarMat?: THREE.MeshStandardMaterial;
  private pillarGlow?: THREE.MeshStandardMaterial;
  private deckGlow?: THREE.MeshStandardMaterial;
  private gantryMat?: THREE.MeshStandardMaterial;

  constructor() {
    this.group.name = 'LightningYard';
    this.initLights();
    this.build();
  }

  // ---------------------------------------------------------------- lumières

  private initLights(): void {
    for (let i = 0; i < LightningYard.LIGHT_SLOTS; i++) {
      const l = new THREE.PointLight(0xffffff, 0, 1, 2);
      l.position.set(0, -999, 0);
      this.group.add(l);
      this.lightPool.push(l);
    }
  }

  /**
   * Réaffecte un slot du pool. On ne touche jamais à `visible` : une lumière
   * masquée sort du compte de three et déclenche la recompilation de TOUS les
   * matériaux (même piège que les PointLights d'items, cf. `item()`).
   */
  private setLight(slot: number, x: number, y: number, z: number, color: number, intensity: number, distance: number, decay = 2): void {
    const l = this.lightPool[slot];
    if (!l) return;
    l.color.setHex(color);
    l.intensity = intensity;
    l.distance = distance;
    l.decay = decay;
    l.position.set(x, y, z);
  }

  // ---------------------------------------------------------------- primitives

  /** MeshStandardMaterial partagé pour un (texture, tiling) donné. */
  private blockMat(name: MaterialName, repeat: number, repeatY?: number): THREE.MeshStandardMaterial {
    const key = `${name}|${repeat}|${repeatY ?? repeat}`;
    let m = this.matCache.get(key);
    if (!m) { m = pbr(name, { repeat, repeatY }); this.matCache.set(key, m); }
    return m;
  }

  /** Matériau émissif partagé (les seams/bandeaux en créaient un par instance). */
  private glowMat(color: number, intensity: number): THREE.MeshStandardMaterial {
    const key = `${color}|${intensity}`;
    let m = this.glowCache.get(key);
    if (!m) { m = SOLID.glow(color, intensity); this.glowCache.set(key, m); }
    return m;
  }

  /** Exclut un objet de la fusion statique (il bouge, clignote ou disparaît). */
  private dynamic<T extends THREE.Object3D>(o: T): T {
    o.userData.dyn = true;
    return o;
  }

  private block(x: number, y: number, z: number, w: number, h: number, d: number, mat: MaterialName, opts: { repeat?: number; repeatY?: number; solid?: boolean; trim?: boolean; shadow?: boolean } = {}): THREE.Mesh {
    const geo = new THREE.BoxGeometry(w, h, d);
    const material = this.blockMat(mat, opts.repeat ?? Math.max(1, Math.round(Math.max(w, d) / 4)), opts.repeatY);
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(x, y, z);
    mesh.castShadow = opts.shadow !== false;
    mesh.receiveShadow = true;
    this.group.add(mesh);
    if (opts.solid !== false) this.addSolid(x - w / 2, y - h / 2, z - d / 2, x + w / 2, y + h / 2, z + d / 2);
    if (opts.trim) {
      const t = 0.12;
      const trimMat = this.trimMat;
      const edges = [
        [x, y + h / 2 - t / 2, z - d / 2 + t / 2, w, t, t],
        [x, y + h / 2 - t / 2, z + d / 2 - t / 2, w, t, t],
        [x - w / 2 + t / 2, y + h / 2 - t / 2, z, t, t, d],
        [x + w / 2 - t / 2, y + h / 2 - t / 2, z, t, t, d],
      ];
      for (const [ex, ey, ez, ew, eh, ed] of edges) {
        const e = new THREE.Mesh(new THREE.BoxGeometry(ew, eh, ed), trimMat);
        e.position.set(ex, ey, ez);
        this.group.add(e);
      }
    }
    return mesh;
  }

  private addSolid(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): void {
    this.solids.push({ min: new THREE.Vector3(x0, y0, z0), max: new THREE.Vector3(x1, y1, z1) });
  }

  /** Plain mesh helper (no collision). */
  private mesh(geo: THREE.BufferGeometry, mat: THREE.Material, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): THREE.Mesh {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z); m.rotation.set(rx, ry, rz);
    this.group.add(m);
    return m;
  }

  /**
   * Guardrail run between two points on the same height (axis-aligned).
   * Posts every ~2 m, brushed top rail, orange emissive mid rail, solid AABB.
   */
  private rail(x1: number, z1: number, x2: number, z2: number, y: number): void {
    const len = Math.hypot(x2 - x1, z2 - z1);
    if (len < 0.4) return;
    const alongX = Math.abs(x2 - x1) > Math.abs(z2 - z1);
    const cx = (x1 + x2) / 2, cz = (z1 + z2) / 2;
    const H = 1.1;
    // top rail + mid glow rail
    const top = new THREE.Mesh(new THREE.BoxGeometry(alongX ? len : 0.07, 0.07, alongX ? 0.07 : len), this.postMat);
    top.position.set(cx, y + H, cz); // pièce fine : pas d'ombre (2× moins de draw calls)
    const mid = new THREE.Mesh(new THREE.BoxGeometry(alongX ? len : 0.035, 0.035, alongX ? 0.035 : len), this.railGlow);
    mid.position.set(cx, y + H * 0.55, cz);
    const low = new THREE.Mesh(new THREE.BoxGeometry(alongX ? len : 0.05, 0.05, alongX ? 0.05 : len), this.railMat);
    low.position.set(cx, y + 0.18, cz);
    this.group.add(top, mid, low);
    // posts
    const n = Math.max(2, Math.round(len / 2) + 1);
    for (let i = 0; i < n; i++) {
      const t = n === 1 ? 0.5 : i / (n - 1);
      const px = x1 + (x2 - x1) * t, pz = z1 + (z2 - z1) * t;
      const post = new THREE.Mesh(new THREE.BoxGeometry(0.09, H, 0.09), this.railMat);
      post.position.set(px, y + H / 2, pz); // montant fin : pas d'ombre
      this.group.add(post);
      const cap = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.04, 0.12), this.railGlow);
      cap.position.set(px, y + H + 0.05, pz);
      this.group.add(cap);
    }
    // collision: thin wall the full run
    const th = 0.14;
    this.addSolid(Math.min(x1, x2) - (alongX ? 0 : th / 2), y, Math.min(z1, z2) - (alongX ? th / 2 : 0), Math.max(x1, x2) + (alongX ? 0 : th / 2), y + H, Math.max(z1, z2) + (alongX ? th / 2 : 0));
  }

  /** Rails around a rectangular platform top, skipping openings. */
  private railsAround(x: number, z: number, w: number, d: number, y: number, openings: RailOpening[] = []): void {
    const inset = 0.22;
    const hw = w / 2 - inset, hd = d / 2 - inset;
    const edges: Array<{ edge: RailOpening['edge']; fixed: number; from: number; to: number; horizontal: boolean }> = [
      { edge: 'n', fixed: z - hd, from: x - hw, to: x + hw, horizontal: true },
      { edge: 's', fixed: z + hd, from: x - hw, to: x + hw, horizontal: true },
      { edge: 'w', fixed: x - hw, from: z - hd, to: z + hd, horizontal: false },
      { edge: 'e', fixed: x + hw, from: z - hd, to: z + hd, horizontal: false },
    ];
    for (const e of edges) {
      const gaps = openings.filter(o => o.edge === e.edge).map(o => [o.at - o.half, o.at + o.half]).sort((a, b) => a[0] - b[0]);
      let cur = e.from;
      for (const [g0, g1] of gaps) {
        if (g0 > cur) this.railSeg(e, cur, g0);
        cur = Math.max(cur, g1);
      }
      if (cur < e.to) this.railSeg(e, cur, e.to);
    }
  }
  private railSeg(e: { fixed: number; horizontal: boolean }, a: number, b: number): void {
    if (b - a < 0.5) return;
    const y = this.currentRailY;
    if (e.horizontal) this.rail(a, e.fixed, b, e.fixed, y);
    else this.rail(e.fixed, a, e.fixed, b, y);
  }
  private currentRailY = 0;

  /** Floating platform: thick slab + underside plate + anti-grav glow + rails. */
  private platform(x: number, y: number, z: number, w: number, d: number, opts: { trim?: boolean; mat?: MaterialName; openings?: RailOpening[]; rails?: boolean; lightSlot?: number } = {}): void {
    const h = 1.2;
    this.block(x, y - h / 2, z, w, h, d, opts.mat ?? 'floor', { trim: opts.trim ?? true });
    this.block(x, y - h - 0.4, z, w * 0.7, 0.8, d * 0.7, 'wall', { solid: false });
    const glow = new THREE.Mesh(
      new THREE.CylinderGeometry(Math.min(w, d) * 0.28, Math.min(w, d) * 0.36, 0.3, 24),
      this.padGlowMat
    );
    glow.position.set(x, y - h - 0.95, z);
    this.group.add(glow);
    // Une seule plateforme (la centrale) consomme un slot du pool ; les autres
    // s'appuient sur le cylindre émissif ci-dessus + bloom, comme les jump pads.
    if (opts.lightSlot !== undefined) {
      this.setLight(opts.lightSlot, x, y - h - 1.4, z, 0x00e5ff, 6, Math.min(w, d) * 1.2);
    }
    // floor light strips (two parallel runway lines)
    this.runway(x, y, z, w, d);
    if (opts.rails !== false) { this.currentRailY = y; this.railsAround(x, z, w, d, y, opts.openings ?? []); }
  }

  /** Small emissive floor studs in two lines across the platform (runway feel). */
  private runway(x: number, y: number, z: number, w: number, d: number): void {
    const studMat = this.studMat;
    const geo = new THREE.BoxGeometry(0.18, 0.03, 0.18);
    const alongX = w >= d;
    const len = alongX ? w : d;
    const n = Math.max(3, Math.floor(len / 2.2));
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n - 0.5;
      for (const side of [-1, 1]) {
        const off = (alongX ? d : w) * 0.28 * side;
        const sx = alongX ? x + t * len : x + off;
        const sz = alongX ? z + off : z + t * len;
        const m = new THREE.Mesh(geo, studMat);
        m.position.set(sx, y + 0.02, sz);
        this.group.add(m);
      }
    }
  }

  /**
   * Stepped ramp (AABB friendly) between two heights, axis-aligned, with
   * sloped side rails (visual) + stepped rail collision + edge lights.
   */
  private ramp(x1: number, y1: number, z1: number, x2: number, y2: number, z2: number, width: number, steps = 8, mat: MaterialName = 'grate', rails = true): void {
    const alongX = Math.abs(x2 - x1) > Math.abs(z2 - z1);
    const total = Math.hypot(x2 - x1, z2 - z1);
    const len = total / steps + 0.06;
    const edgeMat = this.rampEdgeMat;
    // un seul matériau par type de rampe (au lieu d'un par marche)
    let stepMat = this.rampMats.get(mat);
    if (!stepMat) { stepMat = pbr(mat, { repeat: 1 }); this.rampMats.set(mat, stepMat); }
    for (let i = 0; i < steps; i++) {
      const t0 = i / steps, t1 = (i + 1) / steps;
      const cx = THREE.MathUtils.lerp(x1, x2, (t0 + t1) / 2);
      const cz = THREE.MathUtils.lerp(z1, z2, (t0 + t1) / 2);
      const top = THREE.MathUtils.lerp(y1, y2, t1);
      const geo = new THREE.BoxGeometry(alongX ? len : width, 0.5, alongX ? width : len);
      const mesh = new THREE.Mesh(geo, stepMat);
      mesh.position.set(cx, top - 0.25, cz);
      mesh.castShadow = true; mesh.receiveShadow = true;
      this.group.add(mesh);
      const hw = alongX ? len / 2 : width / 2, hd = alongX ? width / 2 : len / 2;
      this.addSolid(cx - hw, top - 0.5, cz - hd, cx + hw, top, cz + hd);
      // edge studs
      if (i % 2 === 0) {
        for (const s of [-1, 1]) {
          const m = new THREE.Mesh(new THREE.BoxGeometry(alongX ? 0.14 : 0.3, 0.03, alongX ? 0.3 : 0.14), edgeMat);
          m.position.set(alongX ? cx : cx + s * (width / 2 - 0.25), top + 0.02, alongX ? cz + s * (width / 2 - 0.25) : cz);
          this.group.add(m);
        }
      }
      if (rails) {
        // stepped collision rail on both sides
        const th = 0.14, H = 1.0;
        for (const s of [-1, 1]) {
          if (alongX) this.addSolid(cx - hw, top, cz + s * (width / 2 - th / 2) - th / 2, cx + hw, top + H, cz + s * (width / 2 - th / 2) + th / 2);
          else this.addSolid(cx + s * (width / 2 - th / 2) - th / 2, top, cz - hd, cx + s * (width / 2 - th / 2) + th / 2, top + H, cz + hd);
          if (i % 2 === 0) {
            const post = new THREE.Mesh(new THREE.BoxGeometry(0.08, H, 0.08), this.railMat);
            post.position.set(alongX ? cx : cx + s * (width / 2 - 0.1), top + H / 2, alongX ? cz + s * (width / 2 - 0.1) : cz);
            this.group.add(post);
          }
        }
      }
    }
    if (rails) {
      // sloped visual top rails (rotated thin boxes, no collision)
      const dy = y2 - y1, slopeLen = Math.hypot(total, dy);
      const ang = Math.atan2(dy, total);
      const dirSign = alongX ? Math.sign(x2 - x1) : Math.sign(z2 - z1);
      for (const s of [-1, 1]) {
        const top = new THREE.Mesh(new THREE.BoxGeometry(slopeLen, 0.07, 0.07), this.postMat);
        const mid = new THREE.Mesh(new THREE.BoxGeometry(slopeLen, 0.035, 0.035), this.railGlow);
        const mx = (x1 + x2) / 2 + (alongX ? 0 : s * (width / 2 - 0.1));
        const mz = (z1 + z2) / 2 + (alongX ? s * (width / 2 - 0.1) : 0);
        const my = (y1 + y2) / 2;
        top.position.set(mx, my + 1.0, mz); mid.position.set(mx, my + 0.55, mz);
        for (const m of [top, mid]) {
          if (alongX) m.rotation.set(0, 0, ang * dirSign);
          else { m.rotation.set(0, Math.PI / 2, 0); m.rotateZ(-ang * dirSign); }
        }
        this.group.add(top, mid);
      }
    }
  }

  private jumpPad(x: number, y: number, z: number, launch: THREE.Vector3, color = 0xf7931a): void {
    const mesh = new THREE.Mesh(
      new THREE.CylinderGeometry(1.4, 1.5, 0.2, 32),
      new THREE.MeshStandardMaterial({ color: 0x0a0a0a, roughness: 0.3, metalness: 0.9, emissive: color, emissiveIntensity: 1.6 })
    );
    mesh.position.set(x, y + 0.1, z);
    this.group.add(this.dynamic(mesh));
    const ring = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.08, 8, 40), new THREE.MeshBasicMaterial({ color: 0xffb84d }));
    ring.rotation.x = Math.PI / 2;
    ring.position.set(x, y + 0.25, z);
    this.group.add(this.dynamic(ring));
    // light column
    const col = new THREE.Mesh(
      new THREE.CylinderGeometry(0.9, 1.3, 4.5, 20, 1, true),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.09, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide })
    );
    col.position.set(x, y + 2.4, z);
    this.group.add(this.dynamic(col));
    // pas de PointLight : le cône + l'anneau émissifs (bloom) suffisent

    const pad: JumpPad = { position: new THREE.Vector3(x, y, z), radius: 1.5, velocity: launch, mesh, ring, lastTrigger: 0 };
    this.jumpPads.push(pad);
    this.animated.push((t) => {
      ring.position.y = y + 0.25 + Math.sin(t * 4) * 0.12;
      ring.scale.setScalar(1 + Math.sin(t * 4) * 0.05);
      (col.material as THREE.MeshBasicMaterial).opacity = 0.06 + (Math.sin(t * 3 + x) * 0.5 + 0.5) * 0.06;
    });
  }

  private teleporter(x: number, y: number, z: number, dest: THREE.Vector3, destYaw: number): void {
    this.block(x, y + 1.7, z, 3.4, 3.6, 0.5, 'panel', { solid: false, repeat: 1 });
    const frameGlow = new THREE.Mesh(new THREE.TorusGeometry(1.55, 0.06, 8, 48), this.glowMat(0x8844ff, 2.5));
    frameGlow.position.set(x, y + 1.7, z);
    this.group.add(this.dynamic(frameGlow));
    const portal = new THREE.Mesh(
      new THREE.CircleGeometry(1.45, 40),
      new THREE.MeshBasicMaterial({ color: 0x8844ff, transparent: true, opacity: 0.75, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false })
    );
    portal.position.set(x, y + 1.7, z);
    this.group.add(this.dynamic(portal));
    // pas de PointLight ici : le pool n'a que 6 slots et le disque additif +
    // l'anneau émissif passent déjà par le bloom
    this.teleporters.push({ position: new THREE.Vector3(x, y, z), radius: 1.2, destination: dest, destYaw, mesh: portal });
    this.animated.push((t) => {
      (portal.material as THREE.MeshBasicMaterial).opacity = 0.55 + Math.sin(t * 6) * 0.15;
      portal.rotation.z = t * 0.8;
      frameGlow.rotation.z = -t * 0.4;
    });
  }

  private item(type: ItemType, x: number, y: number, z: number, amount: number): void {
    const g = new THREE.Group();
    let color = 0xffffff, emissive = 0xffffff, geo: THREE.BufferGeometry;
    switch (type) {
      case 'health': color = 0xff3344; emissive = 0xff3344; geo = new THREE.OctahedronGeometry(0.35); break;
      case 'armor': color = 0xffd700; emissive = 0xffaa00; geo = new THREE.IcosahedronGeometry(0.4, 0); break;
      case 'ammo': color = 0x00e5ff; emissive = 0x00e5ff; geo = new THREE.BoxGeometry(0.5, 0.5, 0.5); break;
      case 'sats': color = 0xf7931a; emissive = 0xf7931a; geo = new THREE.CylinderGeometry(0.35, 0.35, 0.08, 24); break;
      case 'quad': color = 0x66aaff; emissive = 0x2266ff; geo = new THREE.TetrahedronGeometry(0.5); break;
    }
    const core = new THREE.Mesh(geo!, new THREE.MeshStandardMaterial({ color, emissive, emissiveIntensity: 2.2, roughness: 0.2, metalness: 0.8 }));
    g.add(core);
    const halo = new THREE.Mesh(new THREE.RingGeometry(0.6, 0.75, 32), new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.45, side: THREE.DoubleSide, blending: THREE.AdditiveBlending, depthWrite: false }));
    halo.rotation.x = -Math.PI / 2;
    halo.position.y = -0.55;
    g.add(halo);
    // aucune PointLight ici : elle disparaissait de la scène au ramassage
    // (mesh.visible = false) et three recompilait TOUS les matériaux.
    // Le noyau émissif + le halo additif + le bloom rendent le même effet.
    g.position.set(x, y + 0.9, z);
    this.group.add(this.dynamic(g));

    const spawn: ItemSpawn = { type, position: new THREE.Vector3(x, y + 0.9, z), mesh: g, respawnAt: 0, amount };
    this.items.push(spawn);
    const phase = Math.random() * Math.PI * 2;
    this.animated.push((t) => {
      core.rotation.y = t * 1.5 + phase;
      core.rotation.x = Math.sin(t + phase) * 0.3;
      g.position.y = y + 0.9 + Math.sin(t * 2 + phase) * 0.12;
    });
  }

  private spawn(x: number, y: number, z: number, yaw: number): void {
    this.spawns.push({ position: new THREE.Vector3(x, y + 0.1, z), yaw });
  }

  // ---------------------------------------------------------------- station

  /** Axis-aligned hull wall with window band and pillars. `axis` = which coordinate is fixed. */
  private hullWall(axis: 'x' | 'z', fixed: number, from: number, to: number): void {
    const yLo = -9, yWinLo = 5, yWinHi = 17, yHi = 34;
    const len = to - from, mid = (from + to) / 2, th = 2.0;
    // lower band (hull plates) + upper band (concrete panels) — visual only, one slab collision below
    const band = (y0: number, y1: number, mat: MaterialName, ry: number) => {
      const h = y1 - y0;
      if (axis === 'x') this.block(fixed, (y0 + y1) / 2, mid, th, h, len, mat, { repeat: 8, repeatY: ry, solid: false });
      else this.block(mid, (y0 + y1) / 2, fixed, len, h, th, mat, { repeat: 8, repeatY: ry, solid: false });
    };
    band(yLo, yWinLo, 'hull', 2);
    band(yWinHi, yHi, 'cpanel', 3);
    // window band: glass + mullions + light strips above/below
    const gl = this.hullGlass ??= glass(0x6ec6ff, 0.18);
    const pane = new THREE.Mesh(new THREE.PlaneGeometry(len, yWinHi - yWinLo), gl);
    if (axis === 'x') { pane.position.set(fixed - Math.sign(fixed) * (th / 2 - 0.05), (yWinLo + yWinHi) / 2, mid); pane.rotation.y = fixed > 0 ? -Math.PI / 2 : Math.PI / 2; }
    else { pane.position.set(mid, (yWinLo + yWinHi) / 2, fixed - Math.sign(fixed) * (th / 2 - 0.05)); pane.rotation.y = fixed > 0 ? Math.PI : 0; }
    this.group.add(pane);
    // outer dark backing so the window reads as an opening
    const strips = this.hullStrips ??= pbr('trim', { emissive: 0x9be7ff, emissiveIntensity: 1.2 });
    for (const yy of [yWinLo + 0.15, yWinHi - 0.15]) {
      const s = new THREE.Mesh(new THREE.BoxGeometry(axis === 'x' ? 0.15 : len, 0.12, axis === 'x' ? len : 0.15), strips);
      s.position.set(axis === 'x' ? fixed - Math.sign(fixed) * (th / 2 - 0.02) : mid, yy, axis === 'x' ? mid : fixed - Math.sign(fixed) * (th / 2 - 0.02));
      this.group.add(s);
    }
    const n = Math.round(len / 8);
    for (let i = 0; i <= n; i++) {
      const p = from + (len * i) / n;
      const pillarMat = this.pillarMat ??= pbr('painted', { repeat: 1, repeatY: 6 });
      const pillar = new THREE.Mesh(new THREE.BoxGeometry(axis === 'x' ? th + 0.8 : 1.2, yHi - yLo, axis === 'x' ? 1.2 : th + 0.8), pillarMat);
      pillar.position.set(axis === 'x' ? fixed : p, (yLo + yHi) / 2, axis === 'x' ? p : fixed);
      pillar.castShadow = true; pillar.receiveShadow = true;
      this.group.add(pillar);
      // pillar light
      const pl = new THREE.Mesh(new THREE.BoxGeometry(axis === 'x' ? 0.1 : 0.5, 6, axis === 'x' ? 0.5 : 0.1), this.pillarGlow ??= SOLID.glow(0xf7931a, 1.6));
      pl.position.set(axis === 'x' ? fixed - Math.sign(fixed) * (th / 2 + 0.42) : p, 11, axis === 'x' ? p : fixed - Math.sign(fixed) * (th / 2 + 0.42));
      this.group.add(pl);
    }
    // collision: one slab for the whole wall
    if (axis === 'x') this.addSolid(fixed - th / 2 - 0.4, yLo, from - 0.6, fixed + th / 2 + 0.4, yHi, to + 0.6);
    else this.addSolid(from - 0.6, yLo, fixed - th / 2 - 0.4, to + 0.6, yHi, fixed + th / 2 + 0.4);
  }

  /** Animated data panel (scrolling hex, block info) — CanvasTexture refreshed at 2 Hz. */
  private dataPanel(x: number, y: number, z: number, ry: number, w = 3.2, h = 6): void {
    const canvas = document.createElement('canvas');
    canvas.width = 256; canvas.height = 512;
    const ctx = canvas.getContext('2d')!;
    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ map: tex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false }));
    m.position.set(x, y, z); m.rotation.y = ry;
    this.group.add(this.dynamic(m));
    this.dataPanels.push({ ctx, tex, seed: Math.random() * 1000 });
  }

  private drawDataPanels(t: number): void {
    for (const p of this.dataPanels) {
      const { ctx } = p;
      ctx.clearRect(0, 0, 256, 512);
      ctx.fillStyle = 'rgba(8,16,28,0.55)'; ctx.fillRect(0, 0, 256, 512);
      ctx.strokeStyle = 'rgba(155,231,255,0.35)'; ctx.lineWidth = 2; ctx.strokeRect(6, 6, 244, 500);
      ctx.font = 'bold 22px ui-monospace, Menlo, monospace'; ctx.fillStyle = '#ffb84d'; ctx.textAlign = 'left';
      ctx.fillText('MEMPOOL // LIVE', 16, 34);
      ctx.fillStyle = '#9be7ff';
      ctx.font = '15px ui-monospace, Menlo, monospace';
      const rows = 22;
      const scroll = Math.floor(t * 6 + p.seed);
      for (let i = 0; i < rows; i++) {
        const k = (scroll + i) * 2654435761 >>> 0;
        const hex = (k ^ (i * 0x9e3779b9)).toString(16).padStart(8, '0').slice(0, 8);
        const sats = ((k >>> 8) % 97000 + 1200).toLocaleString('fr-FR');
        ctx.globalAlpha = 0.35 + ((i * 7 + scroll) % 5) * 0.13;
        ctx.fillText(`${hex}…  ${sats} sat`, 16, 62 + i * 19);
      }
      ctx.globalAlpha = 1;
      // bar graph
      for (let i = 0; i < 12; i++) {
        const v = 0.3 + 0.7 * Math.abs(Math.sin(t * 1.3 + i * 0.9 + p.seed));
        ctx.fillStyle = i % 3 === 0 ? '#f7931a' : '#2fe0ff';
        ctx.fillRect(16 + i * 19, 496 - v * 40, 12, v * 40);
      }
      if (this.blockHeight) {
        ctx.fillStyle = '#ffffff'; ctx.font = 'bold 18px ui-monospace, Menlo, monospace';
        ctx.fillText(`BLOCK ${this.blockHeight.toLocaleString('fr-FR')}`, 16, 470);
      }
      p.tex.needsUpdate = true;
    }
  }

  // ---------------------------------------------------------------- build

  private build(): void {
    const R = 26, C = 24;

    // ---------------- Central Genesis tower platform (top: y=12)
    this.platform(0, 12, 0, 16, 16, {
      trim: true,
      lightSlot: LightningYard.SLOT.TOWER,
      openings: [{ edge: 'n', at: 0, half: 2.3 }, { edge: 's', at: 0, half: 2.3 }, { edge: 'w', at: 0, half: 2.3 }, { edge: 'e', at: 0, half: 2.3 }],
    });
    this.block(0, 12 + 7, 0, 4, 14, 4, 'panel', { repeat: 2, repeatY: 6 });
    const towerTrim = new THREE.Mesh(new THREE.BoxGeometry(4.3, 0.3, 4.3), this.trimMat);
    towerTrim.position.set(0, 12 + 14.2, 0);
    this.group.add(towerTrim);
    for (let k = 0; k < 4; k++) {
      const a = (k * Math.PI) / 2;
      this.dataPanel(Math.sin(a) * 2.06, 19, Math.cos(a) * 2.06, a, 3.2, 7);
      // vertical light seams on tower corners
      const seam = new THREE.Mesh(new THREE.BoxGeometry(0.08, 13.5, 0.08), this.glowMat(0xf7931a, 1.5));
      seam.position.set(Math.cos(a + Math.PI / 4) * 2.85, 19, Math.sin(a + Math.PI / 4) * 2.85);
      this.group.add(seam);
    }
    this.setLight(LightningYard.SLOT.CORE, 0, 24, 0, 0xf7931a, 20, 40);
    this.group.add(this.dynamic(this.hologram(0, 29, 0)));

    // ---------------- Lower ring: 4 wide platforms at y=4 (N/S/E/W), 26 units out
    // N (z<0) / S (z>0): 14 wide (x), 12 deep (z). Openings: ramp to center, corner ramps.
    this.platform(0, 4, -R, 14, 12, { openings: [{ edge: 's', at: 0, half: 2.3 }, { edge: 'w', at: -24, half: 2.3 }, { edge: 'e', at: -24, half: 2.3 }] });
    this.platform(0, 4, R, 14, 12, { openings: [{ edge: 'n', at: 0, half: 2.3 }, { edge: 'w', at: 24, half: 2.3 }, { edge: 'e', at: 24, half: 2.3 }] });
    // W (x<0) / E (x>0): 12 wide (x), 14 deep (z)
    this.platform(-R, 4, 0, 12, 14, { openings: [{ edge: 'e', at: 0, half: 2.3 }, { edge: 'n', at: -24, half: 2.3 }, { edge: 's', at: -24, half: 2.3 }] });
    this.platform(R, 4, 0, 12, 14, { openings: [{ edge: 'w', at: 0, half: 2.3 }, { edge: 'n', at: 24, half: 2.3 }, { edge: 's', at: 24, half: 2.3 }] });

    // ---------------- Corner platforms at y=8 (diagonals), 24 out
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as Array<[number, number]>) {
      this.platform(sx * C, 8, sz * C, 9, 9, {
        openings: [
          { edge: sx < 0 ? 'e' : 'w', at: sz * C, half: 2.3 },   // toward N/S platform (along x)
          { edge: sz < 0 ? 's' : 'n', at: sx * C, half: 2.3 },   // toward E/W platform (along z)
        ],
      });
    }

    // ---------------- Ramps: lower ring → center (grates)
    // 20 marches → 0,40 m/marche, franchissable (PM.stepHeight = 0,45)
    this.ramp(0, 4, -R + 6, 0, 12, -8.5, 4, 20);
    this.ramp(0, 4, R - 6, 0, 12, 8.5, 4, 20);
    this.ramp(-R + 6, 4, 0, -8.5, 12, 0, 4, 20);
    this.ramp(R - 6, 4, 0, 8.5, 12, 0, 4, 20);

    // ---------------- Ramps: corners (y=8) → lower ring platforms (y=4), 4 m wide with rails
    for (const [sx, sz] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as Array<[number, number]>) {
      // corner → N/S platform (along x): from corner inner edge x=±19.5 to N/S platform edge x=±7
      this.ramp(sx * 19.5, 8, sz * C, sx * 7.2, 4, sz * C, 4, 10, 'grate2');
      // corner → E/W platform (along z)
      this.ramp(sx * C, 8, sz * 19.5, sx * C, 4, sz * 7.2, 4, 10, 'grate2');
    }

    // ---------------- Jump pads: lower ring → tower platform
    // vy=24 (apex +11 m), vxy=16 — clears the 1.1 m rails (crossing at y≈+10.8)
    // and lands ~2.4 m past the platform edge (inside the 16×16 top).
    this.jumpPad(0, 4, -R + 1, new THREE.Vector3(0, 24, 16));
    this.jumpPad(0, 4, R - 1, new THREE.Vector3(0, 24, -16));
    this.jumpPad(-R + 1, 4, 0, new THREE.Vector3(16, 24, 0));
    this.jumpPad(R - 1, 4, 0, new THREE.Vector3(-16, 24, 0));
    // Corner pads: spectacular cross-arena arc onto the center platform top.
    // Apex ≈ y26 (below the gantry), clears every rail; path avoids the tower core.
    const crossV = 10.9, crossVy = 30.5, crossVz = 13.1;
    this.jumpPad(-C, 8, -C, new THREE.Vector3(crossV, crossVy, crossVz));
    this.jumpPad(C, 8, C, new THREE.Vector3(-crossV, crossVy, -crossVz));
    this.jumpPad(C, 8, -C, new THREE.Vector3(-crossV, crossVy, crossVz));
    this.jumpPad(-C, 8, C, new THREE.Vector3(crossV, crossVy, -crossVz));

    // ---------------- Teleporters: corner → center tower
    // dest hors du noyau (AABB x,z ∈ [-2,2], y ∈ [12,26]) : sinon éjection sur le
    // toit + Quad ramassé gratuitement. yaw = atan2(x, z) → regarde le centre.
    this.teleporter(C, 8, -C + 3.5, new THREE.Vector3(0, 12.2, -5), Math.PI);
    this.teleporter(-C, 8, C - 3.5, new THREE.Vector3(0, 12.2, 5), 0);

    // ---------------- Catch deck (y = -6): the safety net. Fall → land → pad back up.
    const D = this.deckY;
    // 94,4 de côté (±47,2) : recouvre les murs (collision à ±46,6) — plus de fente
    this.block(0, D - 0.5, 0, 94.4, 1, 94.4, 'grate2', { repeat: 30, trim: false });
    // deck grid light lines
    const lineMat = this.glowMat(0x00e5ff, 1.0);
    for (let i = -4; i <= 4; i++) {
      const a = new THREE.Mesh(new THREE.BoxGeometry(94.4, 0.03, 0.1), lineMat); a.position.set(0, D + 0.02, i * 10); this.group.add(a);
      const b = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.03, 94.4), lineMat); b.position.set(i * 10, D + 0.02, 0); this.group.add(b);
    }
    // deck return pads (cyan) — outside the ring platforms, arc back onto them
    this.jumpPad(0, D, -38, new THREE.Vector3(0, 30, 9), 0x00e5ff);
    this.jumpPad(0, D, 38, new THREE.Vector3(0, 30, -9), 0x00e5ff);
    this.jumpPad(-38, D, 0, new THREE.Vector3(9, 30, 0), 0x00e5ff);
    this.jumpPad(38, D, 0, new THREE.Vector3(-9, 30, 0), 0x00e5ff);
    // pylons: deck → lower platforms (cover on the deck, visual grounding)
    for (const [px, pz] of [[0, -R], [0, R], [-R, 0], [R, 0]]) {
      this.block(px, (D + 2.8) / 2, pz, 1.6, 2.8 - D, 1.6, 'painted', { repeat: 1, repeatY: 3 });
      // bandeau émissif à la place de la PointLight (budget lumières)
      const pl = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.14, 1.7), this.deckGlow ??= SOLID.glow(0x00e5ff, 2.2));
      pl.position.set(px, D + 2, pz); this.group.add(pl);
    }
    // central deck spire under the tower platform (cover + landmark)
    this.block(0, (D + 9.8) / 2, 0, 3, 9.8 - D, 3, 'panel', { repeat: 1, repeatY: 4 });

    // ---------------- Enclosure: station dock bay walls with window bands
    const E = this.enclosureHalf;
    this.hullWall('x', -E, -E, E);
    this.hullWall('x', E, -E, E);
    this.hullWall('z', -E, -E, E);
    this.hullWall('z', E, -E, E);
    // overhead gantry ring (open sky in the middle for rocket jumps / pads)
    for (const [gx, gz, gw, gd] of [[0, -E + 6, 2 * E, 3], [0, E - 6, 2 * E, 3], [-E + 6, 0, 3, 2 * E], [E - 6, 0, 3, 2 * E]] as Array<[number, number, number, number]>) {
      const g = new THREE.Mesh(new THREE.BoxGeometry(gw, 0.6, gd), this.gantryMat ??= pbr('grate', { repeat: 12 }));
      g.position.set(gx, 33.5, gz); g.castShadow = true;
      this.group.add(g);
      const l = new THREE.Mesh(new THREE.BoxGeometry(gw > gd ? gw : 0.2, 0.15, gw > gd ? 0.2 : gd), this.glowMat(0xffffff, 0.9));
      l.position.set(gx, 33.1, gz);
      this.group.add(l);
    }
    // bay floodlights (4, warm) at the gantry corners — slots 2..5 du pool
    const corners: Array<[number, number]> = [[-1, -1], [1, -1], [-1, 1], [1, 1]];
    for (let i = 0; i < corners.length; i++) {
      const [sx, sz] = corners[i];
      const fx = sx * (E - 6), fz = sz * (E - 6);
      this.setLight(LightningYard.SLOT.BAY + i, fx, 32, fz, 0xffd9a8, 12, 70, 1.6);
      const housing = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.7, 0.6, 12), this.glowMat(0xfff1d6, 3));
      housing.position.set(fx, 31.6, fz);
      this.group.add(housing);
    }

    // ---------------- Items
    this.item('quad', 0, 12, 0, 1);
    this.item('sats', 0, 12, 6, 500);
    this.item('sats', 0, 12, -6, 500);
    this.item('sats', 6, 12, 0, 500);
    this.item('sats', -6, 12, 0, 500);
    this.item('health', 0, 4, -R + 4.5, 25);
    this.item('health', 0, 4, R - 4.5, 25);
    this.item('armor', -R + 4.5, 4, 0, 50);
    this.item('armor', R - 4.5, 4, 0, 50);
    this.item('ammo', -C, 8, -C + 3, 40);
    this.item('ammo', C, 8, C - 3, 40);
    this.item('ammo', C, 8, -C, 40);
    this.item('ammo', -C, 8, C, 40);
    this.item('health', 0, 4, -R - 4, 25);
    this.item('health', 0, 4, R + 4, 25);
    // deck consolation items (you fell — grab something on the way back up)
    this.item('health', 8, D, 8, 25);
    this.item('health', -8, D, -8, 25);
    this.item('ammo', 8, D, -8, 40);
    this.item('armor', -8, D, 8, 25);

    // ---------------- Spawns (yaw = facing center)
    // convention forward = (-sin yaw, sin pitch, -cos yaw) → yaw = atan2(x, z)
    this.spawn(0, 4, -R - 3, Math.PI);
    this.spawn(0, 4, R + 3, 0);
    this.spawn(-R - 2, 4, 0, -Math.PI / 2);
    this.spawn(R + 2, 4, 0, Math.PI / 2);
    this.spawn(-C, 8, -C, -3 * Math.PI / 4);
    this.spawn(C, 8, -C, 3 * Math.PI / 4);
    this.spawn(-C, 8, C, -Math.PI / 4);
    this.spawn(C, 8, C, Math.PI / 4);

    // ---------------- Decorative floating debris (mempool blocks) — outside the windows
    const debrisMats = [pbr('rock', { repeat: 1 }), pbr('concrete', { repeat: 1 })];
    for (let i = 0; i < 28; i++) {
      const s = 0.8 + Math.random() * 3.0;
      const m = new THREE.Mesh(new THREE.BoxGeometry(s, s, s), debrisMats[Math.random() < 0.5 ? 0 : 1]);
      const a = Math.random() * Math.PI * 2;
      const r = 62 + Math.random() * 60;
      m.position.set(Math.cos(a) * r, -20 + Math.random() * 60, Math.sin(a) * r);
      m.rotation.set(Math.random() * 3, Math.random() * 3, 0);
      this.group.add(this.dynamic(m));
      const rs = (Math.random() - 0.5) * 0.4;
      this.animated.push((t, dt) => { m.rotation.y += rs * dt; m.rotation.x += rs * 0.5 * dt; m.position.y += Math.sin(t * 0.3 + a) * 0.002; });
    }

    this.buildGrid();
    this.mergeStatic();
  }

  /**
   * Fusionne toute la géométrie statique en UN mesh par (matériau, flags d'ombre).
   * Sans ça la map partait à ~1 200 draw calls : chaque marche de rampe, chaque
   * montant de garde-corps, chaque stud de piste était un mesh à part.
   *
   * Sont exclus : les objets marqués `dynamic()` (jump pads, portails, items,
   * panneaux data, hologramme, débris) et les matériaux transparents (les vitres
   * de coque, dont le tri alpha dépend de la position réelle du mesh).
   *
   * La physique n'est PAS concernée : collisions, `floorAt()` et les tracés
   * d'armes lisent `solids` / la grille XZ, construits séparément (buildGrid).
   */
  private mergeStatic(): void {
    interface Batch { mat: THREE.Material; cast: boolean; recv: boolean; objs: THREE.Mesh[] }
    const batches = new Map<string, Batch>();

    for (const child of this.group.children) {
      const m = child as THREE.Mesh;
      if (!m.isMesh || m.userData.dyn || Array.isArray(m.material)) continue;
      const mat = m.material as THREE.Material;
      if (mat.transparent) continue;
      const key = `${mat.uuid}|${m.castShadow ? 1 : 0}|${m.receiveShadow ? 1 : 0}`;
      let b = batches.get(key);
      if (!b) { b = { mat, cast: m.castShadow, recv: m.receiveShadow, objs: [] }; batches.set(key, b); }
      b.objs.push(m);
    }

    // 1. fusionner (les géométries sources sont partagées entre lots : on ne
    //    libère rien avant d'avoir tout copié)
    const done: Array<{ batch: Batch; mesh: THREE.Mesh }> = [];
    for (const b of batches.values()) {
      const geos = b.objs.map(m => { m.updateMatrix(); return m.geometry.clone().applyMatrix4(m.matrix); });
      // mergeGeometries renvoie null si les jeux d'attributs diffèrent : dans ce
      // cas on laisse le lot tel quel plutôt que de perdre la géométrie.
      const merged = mergeGeometries(geos, false) as THREE.BufferGeometry | null;
      for (const g of geos) g.dispose();
      if (!merged) continue;
      const mesh = new THREE.Mesh(merged, b.mat);
      mesh.castShadow = b.cast;
      mesh.receiveShadow = b.recv;
      mesh.name = `static_${done.length}`;
      done.push({ batch: b, mesh });
    }

    // 2. remplacer les originaux
    const disposed = new Set<THREE.BufferGeometry>();
    for (const { batch, mesh } of done) {
      this.group.remove(...batch.objs);
      for (const m of batch.objs) {
        if (!disposed.has(m.geometry)) { disposed.add(m.geometry); m.geometry.dispose(); }
      }
      this.group.add(mesh);
    }
  }

  /**
   * Indexe tous les solides dans une grille XZ (cellules de 4 m). Chaque solide
   * est inscrit dans les cellules qu'il recouvre, élargies de 0,3 m pour que les
   * requêtes avec marge (sondes de sol des bots) ne ratent rien en bord de cellule.
   */
  private buildGrid(): void {
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    for (const s of this.solids) {
      if (s.min.x < minX) minX = s.min.x;
      if (s.min.z < minZ) minZ = s.min.z;
      if (s.max.x > maxX) maxX = s.max.x;
      if (s.max.z > maxZ) maxZ = s.max.z;
    }
    if (!Number.isFinite(minX)) { this.grid = []; this.gridNX = this.gridNZ = 0; return; }
    const C = this.CELL, M = 0.3;
    this.gridMinX = minX - M; this.gridMinZ = minZ - M;
    this.gridNX = Math.floor((maxX + M - this.gridMinX) / C) + 1;
    this.gridNZ = Math.floor((maxZ + M - this.gridMinZ) / C) + 1;
    this.grid = new Array(this.gridNX * this.gridNZ);
    for (const s of this.solids) {
      const x0 = Math.max(0, Math.floor((s.min.x - M - this.gridMinX) / C));
      const x1 = Math.min(this.gridNX - 1, Math.floor((s.max.x + M - this.gridMinX) / C));
      const z0 = Math.max(0, Math.floor((s.min.z - M - this.gridMinZ) / C));
      const z1 = Math.min(this.gridNZ - 1, Math.floor((s.max.z + M - this.gridMinZ) / C));
      for (let cz = z0; cz <= z1; cz++) {
        for (let cx = x0; cx <= x1; cx++) {
          const k = cz * this.gridNX + cx;
          (this.grid[k] ??= []).push(s);
        }
      }
    }
  }

  /** Solides candidats à la verticale de (x,z) — une seule cellule de grille. */
  solidsAt(x: number, z: number): Box[] {
    const cx = Math.floor((x - this.gridMinX) / this.CELL);
    const cz = Math.floor((z - this.gridMinZ) / this.CELL);
    if (cx < 0 || cz < 0 || cx >= this.gridNX || cz >= this.gridNZ) return LightningYard.EMPTY;
    return this.grid[cz * this.gridNX + cx] ?? LightningYard.EMPTY;
  }

  /** Solides candidats recouvrant une AABB (collision joueur/bots). */
  queryBox(min: THREE.Vector3, max: THREE.Vector3, out: Box[]): Box[] {
    out.length = 0;
    const C = this.CELL;
    const x0 = Math.max(0, Math.floor((min.x - this.gridMinX) / C));
    const x1 = Math.min(this.gridNX - 1, Math.floor((max.x - this.gridMinX) / C));
    const z0 = Math.max(0, Math.floor((min.z - this.gridMinZ) / C));
    const z1 = Math.min(this.gridNZ - 1, Math.floor((max.z - this.gridMinZ) / C));
    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        const cell = this.grid[cz * this.gridNX + cx];
        if (!cell) continue;
        for (const b of cell) if (out.indexOf(b) < 0) out.push(b);
      }
    }
    return out;
  }

  private hologram(x: number, y: number, z: number): THREE.Mesh {
    this.holoCanvas = document.createElement('canvas');
    this.holoCanvas.width = 1024; this.holoCanvas.height = 256;
    this.holoTex = new THREE.CanvasTexture(this.holoCanvas);
    this.holoTex.colorSpace = THREE.SRGBColorSpace;
    this.drawHologram();
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(20, 5),
      new THREE.MeshBasicMaterial({ map: this.holoTex, transparent: true, side: THREE.DoubleSide, depthWrite: false, blending: THREE.AdditiveBlending })
    );
    mesh.position.set(x, y, z);
    this.animated.push((t) => { mesh.rotation.y = t * 0.3; mesh.position.y = y + Math.sin(t * 0.8) * 0.25; });
    return mesh;
  }

  private drawHologram(): void {
    const ctx = this.holoCanvas.getContext('2d')!;
    ctx.clearRect(0, 0, 1024, 256);
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = 'bold 84px ui-monospace, Menlo, monospace';
    ctx.shadowColor = '#f7931a'; ctx.shadowBlur = 40; ctx.fillStyle = '#ffb84d';
    ctx.fillText(this.blockHeight ? `BLOCK ${this.blockHeight.toLocaleString('fr-FR')}` : 'GENESIS · BLOCK 0', 512, 96);
    ctx.font = '54px ui-monospace, Menlo, monospace';
    ctx.shadowColor = '#00e5ff'; ctx.shadowBlur = 24; ctx.fillStyle = '#7fe8ff';
    ctx.fillText(this.blockHeight ? 'LIGHTNING YARD · LIVE CHAIN' : '₿ 50.00000000', 512, 190);
    this.holoTex.needsUpdate = true;
  }

  /** Feed the live chain tip (from mempool.space) into the hologram + panels. */
  setBlockHeight(h: number): void {
    if (!Number.isFinite(h) || h <= 0 || h === this.blockHeight) return;
    this.blockHeight = h;
    this.drawHologram();
  }

  // ---------------------------------------------------------------- queries

  /** Highest solid top under (x,z) at or below y+0.6 — null if nothing below. */
  floorAt(x: number, y: number, z: number): number | null {
    let best: number | null = null;
    for (const s of this.solidsAt(x, z)) {
      if (x >= s.min.x && x <= s.max.x && z >= s.min.z && z <= s.max.z && s.max.y <= y + 0.6) {
        if (best === null || s.max.y > best) best = s.max.y;
      }
    }
    return best;
  }

  // ---------------------------------------------------------------- runtime

  update(t: number, dt: number): void {
    for (const fn of this.animated) fn(t, dt);
    this.dataT += dt;
    // panneaux data redessinés à 2 Hz (au lieu de ~7) : 4 canvas 256×512 par
    // rafraîchissement, invisible à l'œil mais 3,5× moins de travail CPU/upload
    if (this.dataT > 0.5) { this.dataT = 0; this.drawDataPanels(t); }
    const now = performance.now();
    for (const it of this.items) {
      if (it.respawnAt && now >= it.respawnAt) { it.respawnAt = 0; it.mesh.visible = true; }
    }
  }

  takeItem(it: ItemSpawn, respawnMs: number): void {
    it.mesh.visible = false;
    it.respawnAt = performance.now() + respawnMs;
  }

  randomSpawn(avoid?: THREE.Vector3 | THREE.Vector3[]): SpawnPoint {
    const avoids = avoid ? (Array.isArray(avoid) ? avoid : [avoid]) : [];
    let best = this.spawns[Math.floor(Math.random() * this.spawns.length)];
    if (avoids.length) {
      let bestD = -1;
      for (const s of this.spawns) {
        let minD = Infinity;
        for (const a of avoids) minD = Math.min(minD, s.position.distanceTo(a));
        const d = minD + Math.random() * 8;
        if (d > bestD) { bestD = d; best = s; }
      }
    }
    return best;
  }
}
